import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Toolbar,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Popover,
  Divider,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Footer from "../../../examples/Footer";
import axios from "axios";
import jsPDF from "jspdf";
import "jspdf-autotable";

/**
 * Course Regulation Summary Report
 * - Aggregates courses per regulation/program/year/semester
 * - CSV: course-level rows (exam_course_id, regulation_id, credits)
 * - PDF: summarized table (Regulation, Program, Year, Semester, Total Courses, Total Credits, Approved Courses)
 */
export default function CourseRegulationReport() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [programs, setPrograms] = useState([]);
  const [regulations, setRegulations] = useState([]);

  const [selectedProgram, setSelectedProgram] = useState("");
  const [selectedRegulation, setSelectedRegulation] = useState("");
  const [selectedYear, setSelectedYear] = useState("");

  const [rows, setRows] = useState([]);
  const [rawCourses, setRawCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const user = useMemo(() => {
    const userData = sessionStorage.getItem("user");
    return userData ? JSON.parse(userData) : null;
  }, []);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchPrograms = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/programs/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );
      if (Array.isArray(resp.data)) setPrograms(resp.data);
      else if (resp.data && Array.isArray(resp.data.data)) setPrograms(resp.data.data);
      else setPrograms([]);
    } catch (err) {
      console.error("Error fetching programs", err);
      setPrograms([]);
    }
  }, [user]);

  const fetchRegulations = useCallback(async () => {
    if (!user) return;
    try {
      const resp = await axios.get(
        `${API_BASE_URL}/api/regulations/${user.client_id}/${user.college_id}`,
        { headers: getAuthHeaders() }
      );

      if (Array.isArray(resp.data)) setRegulations(resp.data);
      else if (resp.data && Array.isArray(resp.data.data)) setRegulations(resp.data.data);
      else if (resp.data && Array.isArray(resp.data.items)) setRegulations(resp.data.items);
      else setRegulations([]);
    } catch (err) {
      console.error("Error fetching regulations", err);
      setRegulations([]);
    }
  }, [user]);

  const fetchCourses = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search && search.trim() !== "") params.append("search", search.trim());
      if (selectedProgram) params.append("program_id", selectedProgram);
      if (selectedRegulation) params.append("regulation_id", selectedRegulation);
      if (selectedYear) params.append("year_number", selectedYear);

      const queryString = params.toString();
      const url = queryString
        ? `${API_BASE_URL}/api/course-master/${user.client_id}/${user.college_id}?${queryString}`
        : `${API_BASE_URL}/api/course-master/${user.client_id}/${user.college_id}`;

      const resp = await axios.get(url, { headers: getAuthHeaders() });
      let data = [];
      if (Array.isArray(resp.data)) data = resp.data;
      else if (resp.data && Array.isArray(resp.data.data)) data = resp.data.data;

      setRawCourses(data);

      // aggregate
      const map = new Map();
      data.forEach((c) => {
        const regId = c.regulation_id || c.regulation?.regulation_id || "";
        const progId = c.program_id || c.program?.program_id || "";
        const year = c.year_number ?? c.year ?? "";
        const sem = c.semester_number ?? c.semester ?? "";
        const key = `${regId}||${progId}||${year}||${sem}`;
        const existing = map.get(key) || {
          regulation_id: regId,
          regulation_name: c.regulation_name || c.regulation?.regulation_name || "",
          program_id: progId,
          program_name: c.program_name || c.program?.program_name || "",
          year_number: year,
          semester_number: sem,
          total_courses: 0,
          total_credits: 0,
          approved_courses: 0,
        };
        existing.total_courses += 1;
        existing.total_credits += Number(c.credits || 0);
        if ((c.workflow_status || "").toUpperCase() === "APPROVED") existing.approved_courses += 1;
        map.set(key, existing);
      });

      const agg = Array.from(map.values()).map((v, i) => ({ id: i, ...v }));
      setRows(agg);
    } catch (err) {
      console.error("Error fetching courses for regulation report", err);
      showSnackbar("Error fetching data", "error");
      setRawCourses([]);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, search, selectedProgram, selectedRegulation, selectedYear]);

  useEffect(() => {
    if (user) {
      fetchPrograms();
      fetchRegulations();
      fetchCourses();
    }
  }, [user, fetchPrograms, fetchRegulations, fetchCourses]);

  const handleDownloadCsv = async () => {
    if (!user) return;
    try {
      // CSV: course_id (exam_course_id), regulation_id, credits for currently filtered rawCourses
      const headers = ["exam_course_id", "regulation_id", "credits"];
      const rowsData = rawCourses.map((c) => [
        c.exam_course_id || c.course_id || "",
        c.regulation_id || "",
        c.credits || "",
      ]);

      const csvRows = rowsData.map((r) =>
        r.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(",")
      );
      const csv = [headers.join(","), ...csvRows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "course_regulation_courses.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
      showSnackbar("CSV downloaded", "success");
    } catch (err) {
      console.error("CSV download error", err);
      showSnackbar("Error downloading CSV", "error");
    }
  };

  const handleDownloadPdf = () => {
    try {
      const doc = new jsPDF();
      doc.setFontSize(14);
      doc.text("Course Regulation Summary Report", 14, 20);
      const tableColumn = [
        "Regulation",
        "Program",
        "Year",
        "Semester",
        "Total Courses",
        "Total Credits",
        "Approved Courses",
      ];
      const tableRows = rows.map((r) => [
        r.regulation_name || "",
        r.program_name || "",
        String(r.year_number || ""),
        String(r.semester_number || ""),
        String(r.total_courses || 0),
        String(r.total_credits || 0),
        String(r.approved_courses || 0),
      ]);

      // @ts-ignore
      doc.autoTable({ head: [tableColumn], body: tableRows, startY: 28 });
      doc.save("course_regulation_summary.pdf");
      showSnackbar("PDF downloaded", "success");
    } catch (err) {
      console.error("PDF generation error", err);
      showSnackbar("Error generating PDF", "error");
    }
  };

  const columns = [
    { field: "regulation_name", headerName: "Regulation", flex: 1.2, minWidth: 180 },
    { field: "program_name", headerName: "Program", flex: 1.2, minWidth: 180 },
    { field: "year_number", headerName: "Year", flex: 0.6, minWidth: 90 },
    { field: "semester_number", headerName: "Semester", flex: 0.6, minWidth: 90 },
    { field: "total_courses", headerName: "Total Courses", flex: 0.8, minWidth: 120 },
    { field: "total_credits", headerName: "Total Credits", flex: 0.8, minWidth: 120 },
    { field: "approved_courses", headerName: "Approved Courses", flex: 0.8, minWidth: 120 },
  ];

  return (
    <Box p={2}>
      <Paper elevation={1}>
        <Toolbar sx={{ gap: 1, flexWrap: "wrap" }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            COURSE REGULATION SUMMARY REPORT
          </Typography>

          <Button
            variant="contained"
            startIcon={<MoreVertIcon />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              backgroundColor: "#1976d2",
              color: "#fff",
              "&:hover": { backgroundColor: "#115293" },
            }}
          >
            ACTIONS
          </Button>

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterAnchorEl(e.currentTarget)}
            sx={{
              borderColor: "#1976d2",
              color: "#1976d2",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            FILTERS
            {(selectedProgram || selectedRegulation || selectedYear) && (
              <Chip
                size="small"
                label={
                  (selectedProgram ? 1 : 0) + (selectedRegulation ? 1 : 0) + (selectedYear ? 1 : 0)
                }
                sx={{ ml: 1, backgroundColor: "#1976d2", color: "white" }}
              />
            )}
          </Button>

          <Menu anchorEl={anchorEl} open={openMenu} onClose={() => setAnchorEl(null)}>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                sx={{ backgroundColor: "#4caf50", color: "#fff" }}
                onClick={() => {
                  setAnchorEl(null);
                  handleDownloadCsv();
                }}
              >
                DOWNLOAD CSV
              </Button>
            </MenuItem>
            <MenuItem disableRipple>
              <Button
                fullWidth
                variant="contained"
                startIcon={<PictureAsPdfIcon />}
                sx={{ backgroundColor: "#e53935", color: "#fff" }}
                onClick={() => {
                  setAnchorEl(null);
                  handleDownloadPdf();
                }}
              >
                DOWNLOAD PDF
              </Button>
            </MenuItem>
          </Menu>

          <Popover
            open={Boolean(filterAnchorEl)}
            anchorEl={filterAnchorEl}
            onClose={() => setFilterAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{
              sx: {
                boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
                borderRadius: 1,
                border: "1px solid #e0e0e0",
                background: "#ffffff",
                minWidth: 240,
                maxWidth: 360,
              },
            }}
          >
            <Box sx={{ p: 1.5, background: "#ffffff" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1.5,
                  pb: 1,
                  borderBottom: "1px solid #f0f0f0",
                }}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: "#1976d2",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                  }}
                >
                  Filters
                </Typography>
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedProgram("");
                    setSelectedRegulation("");
                    setSelectedYear("");
                  }}
                  sx={{
                    color: "#d32f2f",
                    fontSize: "0.75rem",
                    minWidth: "auto",
                    p: 0.5,
                    "&:hover": { backgroundColor: "#ffebee" },
                  }}
                >
                  Clear
                </Button>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Program
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedProgram || ""}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Programs --</option>
                  {programs.map((p) => (
                    <option key={p.program_id || p.id} value={p.program_id || p.id}>
                      {p.program_name || p.name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Regulation
                </Typography>
                <TextField
                  select
                  fullWidth
                  size="small"
                  value={selectedRegulation || ""}
                  onChange={(e) => setSelectedRegulation(e.target.value)}
                  SelectProps={{ native: true }}
                  sx={{ backgroundColor: "#f8f9fa", borderRadius: 0.5 }}
                >
                  <option value="">-- All Regulations --</option>
                  {regulations.map((r) => (
                    <option key={r.regulation_id || r.id} value={r.regulation_id || r.id}>
                      {r.regulation_name || r.name}
                    </option>
                  ))}
                </TextField>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    mb: 0.5,
                    color: "#666",
                    fontWeight: 600,
                    fontSize: "0.8rem",
                    display: "block",
                  }}
                >
                  Year
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="e.g. 1"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                />
              </Box>
            </Box>
          </Popover>

          <TextField
            size="small"
            placeholder="Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={fetchCourses}>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Toolbar>

        <Box sx={{ height: 560, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25]}
            disableSelectionOnClick
          />
        </Box>
      </Paper>

      <Footer />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
}
